import { AxiosError } from 'axios'
import { useDialog } from 'primevue/usedialog'
import { RestaurantAreas } from '~/lib/interfaces/restaurant'
import { DLVModalAction } from '~/lib/models/dlv-modal'
import { FavoriteAddress } from '~/lib/services/store/auth.dto'
import { Address } from '~/lib/services/store/order/order.dto'

export const useAddress = (countryCode: string) => {
  const restaurantStore = useRestaurantStore()
  const { restaurantServiceRef } = useStoreService()
  const restaurantActions = useRestaurantActions()
  const { trackNoRider, trackNoCoverage, trackConfirmAddress, trackSaveNewAddress } = useGTM()
  const dialog = useDialog()
  const { t } = useLocale()

  const restaurantService = restaurantServiceRef()

  const selectAddress = async (addrs: Address) => {
    try {
      trackConfirmAddress()
      const restaurant = await restaurantService.getRestaurantDelivery({
        latitude: String(addrs.latitude),
        longitude: String(addrs.longitude),
        countryCode,
        area: RestaurantAreas.DLV,
      })

      restaurantStore.restaurantTips = restaurant.tips
      restaurantStore.candidateArea = RestaurantAreas.DLV
      restaurantStore.provisionalAddress = addrs
      await restaurantActions.setRestaurant(restaurant)
      // Get if order will be delivered by walking crew
      restaurantActions.setCrewStatus(restaurant?.isCrew)
      restaurantActions.setDeliveryDistance(restaurant.distance)
    } catch (err: unknown) {
      if (err instanceof AxiosError) {
        const status = err?.response?.status ?? 500
        // TODO: Change this to allow user to select restaurant for errors 613 and 618, when the backend updates the endpoint
        let alertKey = 'genericError'
        let headerImage = '/images/icons/area/sign.svg'
        let customCssClass

        switch (status) {
          case 605:
            trackNoCoverage()
            alertKey = 'mcDeliveryError'
            headerImage = '/images/icons/area/no_coverage.svg'
            customCssClass = '[&_img]:!w-60'
            break
          case 613:
            trackNoRider()
            alertKey = 'ridersError'
            break
          case 618:
            alertKey = 'restaurantClosedError'
            break
        }

        const result = await showDialogAlert({
          dialog,
          data: {
            title: t(`alertDialog.${alertKey}.title`),
            body: t(`alertDialog.${alertKey}.body`),
            btnPrimary: t(`alertDialog.${alertKey}.btnPrimary`),
            btnSecondary: t(`alertDialog.${alertKey}.btnSecondary`),
            headerImage,
          },
          customCssClass,
        })

        restaurantStore.closeModal()
        const willOpenAreaModal = result ? RestaurantAreas.MOP : RestaurantAreas.DLV
        await restaurantStore.setArea({ area: willOpenAreaModal, updateGoogleMaps: true })
        if (result) restaurantStore.openModal()
        else restaurantStore.openChangeRestaurantModal()
      } else {
        // eslint-disable-next-line no-console
        console.log(err)
      }
    }
  }

  /* For favorite addresses (MySavedAddresses) */

  const onAddNewAddress = async () => {
    trackSaveNewAddress('add')
    await restaurantStore.setArea({ area: RestaurantAreas.DLV, updateGoogleMaps: true })
    restaurantStore.setDLVModalState({
      action: DLVModalAction.ONLY_ADD_FAVORITE,
      address: undefined,
    })
    restaurantStore.openChangeRestaurantModal()
  }

  const onEditAddress = async (favoriteAddress: FavoriteAddress) => {
    trackSaveNewAddress('modify')
    await restaurantStore.setArea({ area: RestaurantAreas.DLV, updateGoogleMaps: true })
    restaurantStore.setDLVModalState({
      action: DLVModalAction.ONLY_EDIT_FAVORITE_ADDRESS,
      address: favoriteAddress,
    })
    restaurantStore.openModal()
  }

  const setAnonymousAddressById = (favoriteAddressId: string) => {
    if (restaurantStore.selectedRestaurant?.addressDLV?.favoriteId === favoriteAddressId)
      restaurantStore.setAnonymSelectedAddress()
  }

  return {
    selectAddress,
    // for favorite addresses on list
    onAddNewAddress,
    onEditAddress,
    setAnonymousAddressById,
  }
}
